import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Grid, List, ListItem, Stack, ToggleButton, Select, MenuItem, FormControl, Divider, Accordion, AccordionSummary, AccordionDetails, SwipeableDrawer, ListItemText, ListItemSecondaryAction, ListItemButton, InputBase, Paper} from "@mui/material";
// Removed react-intersection-observer to prevent ResizeObserver errors
// import axios from "axios";
import {config} from "../config";
import TextField from "@mui/material/TextField";
import QTooltip from "../components/Tooltips/QTooltip";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import useEventListener from "@use-it/event-listener";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Drawer from "@mui/material/Drawer";
import {AiOutlineCaretRight} from "react-icons/ai";
import {FiArrowUp} from "react-icons/fi";
import Typography from "@mui/material/Typography";
import Markdown from "react-markdown";
import "./qPilotStyles.css";
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import 'katex/dist/katex.min.css'
import {useAppContext} from "../libs/appContextLib";
import { sanitizeUserInput } from '../utils/sanitization';
import useWebSocket, {ReadyState} from "react-use-websocket";
// import {usePreviousValue} from "../libs/usePreviousValue";
import Chip from "@mui/material/Chip";
import { Link as RouterLink } from 'react-router-dom';
import {keyframes} from '@mui/system';
import { useFileUploads } from '../hooks/useFileUploads';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { AttachmentTray } from '../components/FileAttachments/AttachmentTray';
import { AttachButton } from '../components/FileAttachments/AttachButton';
import { DragDropOverlay } from '../components/FileAttachments/DragDropOverlay';
import IconButton from "@mui/material/IconButton";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MarkdownTable from '../components/MarkdownTable';
import { QPilotChart } from '../components/QPilotCharts';
// New imports for session management
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ChatIcon from "@mui/icons-material/Chat";
import { getIdJwtToken } from '../utils/cognitoTokens';

const QBearMascot = "/images/qpilot-avatar-image.png";

// Generate a UUID v4 for session IDs
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// Timeout thresholds for WebSocket responses
const SINGLE_STATUS_TIMEOUT_SECONDS = 45; // Max time for any single loading status
const TOTAL_RESPONSE_TIMEOUT_SECONDS = 300; // Max total response time (5 minutes)





// ATTACHMENTS LOGIC START
const attachmentsDisabled = true;
// ATTACHMENTS LOGIC END


// Helper function to check if a URL is internal
const isInternalUrl = (url) => {
  // Remove any query parameters and hash
  const cleanUrl = url.split('?')[0].split('#')[0];

  // Check if it's a relative URL
  if (cleanUrl.startsWith('/')) {
    return true;
  }

  // Check if it matches current domain
  if (cleanUrl.includes(window.location.hostname)) {
    return true;
  }

  // Check if it matches qflow.ai domain patterns
  const qflowDomains = ['qflow.ai', 'test.qflow.ai'];
  try {
    const urlObj = new URL(cleanUrl);
    if (qflowDomains.includes(urlObj.hostname)) {
      return true;
    }
  } catch (e) {
    // If URL parsing fails, it's not a valid URL
    return false;
  }

  return false;
};

// Helper function to render diagnostic chips
const DiagnosticChips = ({ diagnostics }) => {
    if (!diagnostics || process.env.REACT_APP_STAGE === 'prod') {
        return null;
    }

    const renderChip = (label, value, color = 'default') => {
        if (value === undefined || value === null) return null;

        const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

        return (
            <Chip
                key={label}
                label={`${label}: ${displayValue}`}
                size="small"
                variant="outlined"
                color={color}
                sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    '& .MuiChip-label': {
                        paddingLeft: '6px',
                        paddingRight: '6px'
                    }
                }}
            />
        );
    };

    // Handle both old and new diagnostic formats
    const hasOldFormat = diagnostics.method_used || diagnostics.ab_test_group || diagnostics.cache_hit !== undefined;
    const hasNewFormat = diagnostics.question_id || diagnostics.db_entries;
    const hasNewDiagnosticFormat = diagnostics.tool_selection || diagnostics.cache_metrics || diagnostics.execution_details;

    return (
        <Box sx={{ mt: 1, mb: 0.5 }}>
            {/* Main chips row - combines all top-level diagnostic chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {/* Response time chip - always show first if available */}
                {diagnostics.response_time_seconds && renderChip('Response Time', `${diagnostics.response_time_seconds}s`, 'info')}
                {diagnostics.performance?.total_response_time_ms && renderChip('Total Server Time', `${(diagnostics.performance.total_response_time_ms / 1000).toFixed(2)}s`, 'info')}

                {/* New diagnostic format chips */}
                {diagnostics.tool_selection?.selected_tool && renderChip('Tool', diagnostics.tool_selection.selected_tool, 'primary')}
                {diagnostics.tool_selection?.method_used && renderChip('Method', diagnostics.tool_selection.method_used, 'secondary')}
                {diagnostics.cache_metrics?.cache_hit !== undefined && renderChip('Cache', diagnostics.cache_metrics.cache_hit ? 'Hit' : `Miss`, diagnostics.cache_metrics.cache_hit ? 'success' : 'warning')}
                {diagnostics.execution_details?.total_llm_calls > 0 && renderChip('LLM Calls', diagnostics.execution_details.total_llm_calls, 'info')}
                {diagnostics.execution_details?.total_tokens_used > 0 && renderChip('Tokens', diagnostics.execution_details.total_tokens_used, 'info')}

                {/* Old format chips */}
                {!hasNewDiagnosticFormat && diagnostics.method_used && renderChip('Method', diagnostics.method_used, 'primary')}
                {!hasNewDiagnosticFormat && diagnostics.ab_test_group && renderChip('A/B Group', diagnostics.ab_test_group, 'secondary')}
                {!hasNewDiagnosticFormat && diagnostics.cache_hit !== undefined && renderChip('Cache', diagnostics.cache_hit ? 'Hit' : `Miss (${diagnostics.cache_miss_reason || 'unknown'})`, diagnostics.cache_hit ? 'success' : 'warning')}

                {/* New format chips */}
                {diagnostics.question_id && renderChip('Question ID', diagnostics.question_id.substring(0, 8) + '...', 'primary')}
                {diagnostics.db_entries && renderChip('DB Entries', diagnostics.db_entries.length, 'secondary')}

                {/* Errors and warnings */}
                {diagnostics.errors?.length > 0 && renderChip('Errors', diagnostics.errors.length, 'error')}
                {diagnostics.warnings?.length > 0 && renderChip('Warnings', diagnostics.warnings.length, 'warning')}

                {/* Feature flags - show if any are enabled */}
                {diagnostics.feature_flags?.parallel_processing_enabled && renderChip('Parallel', 'On', 'success')}
                {diagnostics.feature_flags?.function_calling_enabled && renderChip('Functions', 'On', 'success')}
                {diagnostics.feature_flags?.chart_generation_enabled && renderChip('Charts', 'On', 'success')}
            </Box>

            {/* Collapsible detailed diagnostics */}
            {(diagnostics.tools_considered || diagnostics.phase_timings || diagnostics.llm_calls || diagnostics.db_entries ||
              diagnostics.errors?.length > 0 || diagnostics.warnings?.length > 0 || diagnostics.context_resolution) && (
                <Accordion sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    backgroundColor: 'transparent'
                }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
                        sx={{
                            minHeight: 'auto',
                            '& .MuiAccordionSummary-content': {
                                margin: '8px 0'
                            },
                            padding: 0
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Detailed Diagnostics
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0, paddingTop: 1 }}>
                        <Stack spacing={1}>
                            {/* Old format detailed diagnostics */}
                            {diagnostics.tools_considered && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        Tools Considered:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {diagnostics.tools_considered.map((tool, index) => (
                                            <Chip
                                                key={index}
                                                label={`${tool.name}: ${tool.score}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {diagnostics.phase_timings && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        Phase Timings:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {Object.entries(diagnostics.phase_timings).map(([phase, timing]) => (
                                            <Chip
                                                key={phase}
                                                label={`${phase}: ${timing}ms`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {diagnostics.llm_calls && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        LLM Calls:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {diagnostics.llm_calls.map((call, index) => (
                                            <Chip
                                                key={index}
                                                label={`${call.model}: ${call.tokens}t, ${call.latency}ms`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* New format detailed diagnostics - DB Entries */}
                            {diagnostics.db_entries && diagnostics.db_entries.length > 0 && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        Database Entries Retrieved:
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {diagnostics.db_entries.map((entry, index) => (
                                            <Box key={index} sx={{
                                                p: 1,
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                borderRadius: 1,
                                                border: '1px solid rgba(0,0,0,0.05)'
                                            }}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                                    <Chip
                                                        label={`Type: ${entry.type || 'unknown'}`}
                                                        size="small"
                                                        sx={{ fontSize: '0.6rem', height: '16px' }}
                                                    />
                                                    {entry.cmetadata?.tool_type && (
                                                        <Chip
                                                            label={`Tool: ${entry.cmetadata.tool_type}`}
                                                            size="small"
                                                            sx={{ fontSize: '0.6rem', height: '16px' }}
                                                        />
                                                    )}
                                                    {entry.cmetadata?.entity_type && (
                                                        <Chip
                                                            label={`Entity: ${entry.cmetadata.entity_type}`}
                                                            size="small"
                                                            sx={{ fontSize: '0.6rem', height: '16px' }}
                                                        />
                                                    )}
                                                </Box>
                                                {entry.cmetadata?.summary && (
                                                    <Typography variant="caption" sx={{
                                                        fontSize: '0.6rem',
                                                        color: 'text.secondary',
                                                        display: 'block',
                                                        mt: 0.5
                                                    }}>
                                                        {entry.cmetadata.summary}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* New diagnostic format - Context Resolution */}
                            {diagnostics.context_resolution && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        Context Resolution:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {diagnostics.context_resolution.entity_types?.map((type, index) => (
                                            <Chip
                                                key={index}
                                                label={`Entity: ${type}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                        ))}
                                        {diagnostics.context_resolution.analytic_intents?.map((intent, index) => (
                                            <Chip
                                                key={index}
                                                label={`Intent: ${intent}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Errors */}
                            {diagnostics.errors?.length > 0 && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: 'error.main' }}>
                                        Errors:
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {diagnostics.errors.map((error, index) => (
                                            <Typography key={index} variant="caption" sx={{ fontSize: '0.6rem', color: 'error.main' }}>
                                                • {error}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Warnings */}
                            {diagnostics.warnings?.length > 0 && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: 'warning.main' }}>
                                        Warnings:
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {diagnostics.warnings.map((warning, index) => (
                                            <Typography key={index} variant="caption" sx={{ fontSize: '0.6rem', color: 'warning.main' }}>
                                                • {warning}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
};

export default function QPilotChatbot(props) {


    const [currentPrompt, setCurrentPrompt] = React.useState("")
    const [selectedChannel, setSelectedChannel] = React.useState("chat-stream");
    const previousChannelRef = useRef(null);

    //Public API that will echo messages sent to it back to the client
    const [socketUrl, setSocketUrl] = useState(config.websocket.URL);
    const [messageHistory, setMessageHistory] = useState([{
        type: "divider",
        message: `Using ${selectedChannel}`,
        timestamp: new Date().toISOString()
    }]);
    const [aiMessage, setAiMessage] = useState("");

    // Connection health monitoring state
    const [connectionState, setConnectionState] = useState('connecting'); // 'connecting', 'connected', 'reconnecting', 'disconnected'
    const [lastActivity, setLastActivity] = useState(Date.now());
    const heartbeatIntervalRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const isPageVisibleRef = useRef(true);
    const tokenRefreshTimeoutRef = useRef(null);

    // Timer for tracking response time
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const [responseStartTime, setResponseStartTime] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    // Timeout warning states
    const [showSingleStatusTimeout, setShowSingleStatusTimeout] = useState(false);
    const [showTotalTimeout, setShowTotalTimeout] = useState(false);
    const [currentStatusStartTime, setCurrentStatusStartTime] = useState(null);

    // Mobile keyboard visibility tracking - simplified
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    // Constants for connection management
    const HEARTBEAT_INTERVAL = 30000; // 30 seconds
    const INACTIVITY_TIMEOUT = 120000; // 2 minutes
    const RECONNECT_DELAY = 3000; // 3 seconds
    const TOKEN_REFRESH_INTERVAL = 3300000; // 55 minutes (tokens typically expire at 60 mins)

    // Session management state (Phase 1 - only active for chat-stream-v2)
    const [currentSessionId, setCurrentSessionId] = useState(() => {
        // Load from localStorage if available and channel is chat-stream-v2
        if (selectedChannel === 'chat-stream-v2') {
            const stored = localStorage.getItem('qpilot-current-session-id');
            return stored || null;
        }
        return null;
    });
    const [sessions, setSessions] = useState(() => {
        // Load sessions from localStorage or use placeholder data
        const stored = localStorage.getItem('qpilot-sessions');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Failed to parse stored sessions:', e);
            }
        }
        // Placeholder data with proper UUIDs
        return [
            {
                id: generateUUID(),
                name: 'ARR Analysis Discussion',
                lastModified: '2025-01-19T10:30:00Z',
                status: 'active',
                preview: 'What was our ARR last quarter?',
                messageCount: 42
            },
            {
                id: generateUUID(),
                name: 'Pipeline Review',
                lastModified: '2025-01-18T15:45:00Z',
                status: 'archived',
                preview: 'Show me deals closing this month...',
                messageCount: 15
            }
        ];
    });
    const [showSessionManager, setShowSessionManager] = useState(false);
    const [sessionSearchQuery, setSessionSearchQuery] = useState("");
    const [sessionFilter, setSessionFilter] = useState("all"); // all, active, archived

    const handleCurrentPrompt = React.useCallback((e) => {
        setCurrentPrompt(String(e.target.value || ""))
    }, [])


    function getOS() {
        const userAgent = window.navigator.userAgent,
            platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
            macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
            windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
            iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        let os = null;

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = 'Mac OS';
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = 'iOS';
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = 'Windows';
        } else if (/Android/.test(userAgent)) {
            os = 'Android';
        } else if (/Linux/.test(platform)) {
            os = 'Linux';
        }

        return os;
    }

    const operatingSystem = getOS()

    const messagesEndRef = useRef(null)
    const messageContainerRef = useRef(null)
    const [isNearBottom, setIsNearBottom] = useState(true)
    const checkScrollPositionRef = useRef(null)
    const lastManualToggleRef = useRef(null) // Tracks timestamp of last manual focus mode toggle

    // Check if user is near bottom of scroll container
    const checkIfNearBottom = useCallback(() => {
        if (!messageContainerRef.current || !props.showQPilotChat) return;

        const container = messageContainerRef.current;
        const threshold = 100; // pixels from bottom
        const isNear = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
        setIsNearBottom(isNear);
    }, [props.showQPilotChat]);

    // Debounced scroll handler
    const handleScroll = useCallback(() => {
        if (checkScrollPositionRef.current) {
            clearTimeout(checkScrollPositionRef.current);
        }
        checkScrollPositionRef.current = setTimeout(() => {
            checkIfNearBottom();
        }, 50); // 50ms debounce
    }, [checkIfNearBottom]);

    // Set up scroll listener
    useEffect(() => {
        if (!props.showQPilotChat) return;

        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const container = messageContainerRef.current;
            if (!container) return;

            // Check initial position
            checkIfNearBottom();

            // Add scroll listener
            container.addEventListener('scroll', handleScroll, { passive: true });
        }, 100);

        return () => {
            clearTimeout(timer);
            const container = messageContainerRef.current;
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
            if (checkScrollPositionRef.current) {
                clearTimeout(checkScrollPositionRef.current);
            }
        };
    }, [props.showQPilotChat, handleScroll, checkIfNearBottom]);

    const scrollToBottom = useCallback((force = false) => {
        if (!props.showQPilotChat || !messageContainerRef.current) return;

        if (force || isNearBottom) {
            // Use scrollTop for more reliable scrolling
            const container = messageContainerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    }, [isNearBottom, props.showQPilotChat])

    // Debounced scroll for streaming messages
    const debouncedScrollRef = useRef(null)

    useEffect(() => {
        if (!props.showQPilotChat) return; // Skip if drawer is closed

        // Clear any pending debounced scroll
        if (debouncedScrollRef.current) {
            clearTimeout(debouncedScrollRef.current)
        }

        // Only debounce during AI streaming, immediate scroll for message history changes
        if (aiMessage) {
            debouncedScrollRef.current = setTimeout(() => {
                scrollToBottom()
            }, 100) // 100ms debounce during streaming
        }

        return () => {
            if (debouncedScrollRef.current) {
                clearTimeout(debouncedScrollRef.current)
            }
        }
    }, [aiMessage, scrollToBottom, props.showQPilotChat])

    // Immediate scroll for new messages in history
    useEffect(() => {
        if (props.showQPilotChat) {
            scrollToBottom()
        }
    }, [messageHistory, scrollToBottom, props.showQPilotChat]);


    // Timer effect for tracking response time and timeouts
    useEffect(() => {
        let interval = null;
        if (isWaitingForResponse && responseStartTime) {
            interval = setInterval(() => {
                const now = Date.now();
                const totalElapsed = Math.floor((now - responseStartTime) / 1000);
                setElapsedSeconds(totalElapsed);

                // Check for total timeout (5 minutes)
                if (totalElapsed >= TOTAL_RESPONSE_TIMEOUT_SECONDS && !showTotalTimeout) {
                    setShowTotalTimeout(true);
                }

                // Check for single status timeout (45 seconds)
                if (currentStatusStartTime) {
                    const statusElapsed = Math.floor((now - currentStatusStartTime) / 1000);
                    if (statusElapsed >= SINGLE_STATUS_TIMEOUT_SECONDS && !showSingleStatusTimeout) {
                        setShowSingleStatusTimeout(true);
                    }
                }
            }, 100); // Update every 100ms for smooth display
        }
        return () => clearInterval(interval);
    }, [isWaitingForResponse, responseStartTime, currentStatusStartTime, showTotalTimeout, showSingleStatusTimeout]);

    // Simplified mobile keyboard detection
    useEffect(() => {
        if (!props.isMobile) return;

        const handleInputFocus = () => {
            setIsKeyboardVisible(true);
            // Prevent body scrolling when keyboard is visible
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        };

        const handleInputBlur = () => {
            setIsKeyboardVisible(false);
            // Restore body scrolling
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };

        const inputElement = document.getElementById('qpilot_active_chat');
        if (inputElement) {
            inputElement.addEventListener('focus', handleInputFocus);
            inputElement.addEventListener('blur', handleInputBlur);

            return () => {
                inputElement.removeEventListener('focus', handleInputFocus);
                inputElement.removeEventListener('blur', handleInputBlur);
                // Clean up body styles
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
            };
        }
    }, [props.isMobile]);

    function handler(event) {
        const {key} = event;
        // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
        const isModifierEnter = (event.metaKey || event.ctrlKey) && String(key) === "Enter";

        // Handle ESC key for focus mode
        if (String(key) === "Escape" && props.isFocusMode) {
            props.onToggleFocus();
            return;
        }

        if (isModifierEnter && !isWaitingForResponse && ((typeof currentPrompt === 'string' && currentPrompt.trim()) || fileUploads.files.length > 0) && !fileUploads.hasUploading && (fileUploads.files.length === 0 || fileUploads.allUploaded)) {
            handleClickSendMessage()
            setCurrentPrompt("")
        }
    }

    useEventListener('keydown', handler);

    const remarkMathOptions = {
        singleDollarTextMath: false,
    };

    const processText = (content) => {
        return content
            .replace(/\[/g, '$$$') // Replace all occurrences of [ with $$
            .replace(/\]/g, '$$$') // Replace all occurrences of ] with $$
            .replace(/\(/g, '$$$') // Replace all occurrences of ( with $$
            .replace(/\)/g, '$$$'); // Replace all occurrences of ) with $$
    }

    // Auto-focus callback - triggers focus mode when content exceeds drawer width
    const handleAutoFocus = useCallback(() => {
        if (!props.isFocusMode && props.onToggleFocus) {
            props.onToggleFocus();
        }
    }, [props.isFocusMode, props.onToggleFocus]);

    // Custom components for Markdown
    const components = {
        a: ({node, ...props}) => {
            const href = props.href;
            if (isInternalUrl(href)) {
                try {
                    // If it's a full URL, extract just the pathname
                    const urlObj = new URL(href);
                    const to = urlObj.pathname + urlObj.search + urlObj.hash;
                    return <RouterLink {...props} to={to} style={{ textDecoration: 'none', color: '#4DA685' }} />;
                } catch (e) {
                    // If URL parsing fails, it's likely a relative URL, use it as is
                    return <RouterLink {...props} to={href} style={{ textDecoration: 'none', color: '#4DA685' }} />;
                }
            }
            return <a {...props} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4DA685' }} />;
        },
        table: ({node, ...props}) => {
            return <MarkdownTable {...props} onWidthOverflow={handleAutoFocus} />;
        }
    };


    // WEBSOCKET LOGIC START

    // Auto-activate focus when a QPilot response includes a table or chart
    useEffect(() => {
        // Early bailout - don't process if already in focus mode
        if (props.isFocusMode || !props.onToggleFocus || !messageHistory || messageHistory.length === 0) {
            return;
        }

        // Respect recent manual toggles - don't auto-activate if user just manually toggled
        const timeSinceManualToggle = lastManualToggleRef.current ? Date.now() - lastManualToggleRef.current : Infinity;
        if (timeSinceManualToggle < 500) { // 500ms cooldown
            return;
        }

        const lastMessage = messageHistory[messageHistory.length - 1];

        // Only process completed QPilot messages
        if (!lastMessage ||
            lastMessage.sender?.sender_id !== "qpilot" ||
            lastMessage.other_data?.is_streaming === true) {
            return;
        }

        // Detect markdown table pattern and charts
        const hasMarkdownTable = typeof lastMessage.message === 'string' && /(^|\n)\s*\|.*\|\s*\n\s*\|[-:| ]+\|/m.test(lastMessage.message);
        const hasCharts = (lastMessage.chart_spec && lastMessage.visualization_enabled) || (Array.isArray(lastMessage.charts) && lastMessage.charts.length > 0);

        if (hasMarkdownTable || hasCharts) {
            props.onToggleFocus();
        }
    }, [messageHistory, props.isFocusMode, props.onToggleFocus]);

    // Helper function to set AI message and track status change time
    const setAiMessageWithTimeout = useCallback((message) => {
        setAiMessage(message);
        setCurrentStatusStartTime(Date.now());
        // Reset single status timeout when changing status
        setShowSingleStatusTimeout(false);
    }, []);

    const {currUser, forecastPeriodPreference, qPilotDrawerWidth} = useAppContext();

    // Helper function to refresh auth token
    const refreshAuthToken = useCallback(async () => {
        try {
            const token = await getIdJwtToken();
            return token;
        } catch (error) {
            console.error('Failed to refresh auth token:', error);
            setConnectionState('disconnected');
            return null;
        }
    }, []);

    const {
        sendJsonMessage,
        lastJsonMessage,
        sendMessage,
        lastMessage,
        readyState,
        getWebSocket
    } = useWebSocket(socketUrl, {
        filter: false,
        share: false,
        queryParams: {
            jwt: currUser.authToken,
            channel: selectedChannel
        },
        onOpen: () => {
            
            setConnectionState('connected');
            setLastActivity(Date.now());

            // Clear any pending reconnect attempts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        },
        onClose: () => {
            
            setConnectionState('disconnected');
        },
        onError: (error) => {
            console.error('QPilot WebSocket error:', error);
            setConnectionState('reconnecting');
        },
        shouldReconnect: (closeEvent) => {
            // Always attempt to reconnect unless page is hidden
            return isPageVisibleRef.current;
        },
        reconnectAttempts: 10,
        reconnectInterval: (attemptNumber) => {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, then stay at 30s
            return Math.min(1000 * Math.pow(2, attemptNumber), 30000);
        },

        onMessage: (rawMessage) => {
            const message = JSON.parse(rawMessage.data)

            // Update last activity timestamp
            setLastActivity(Date.now());

            // Handle pong response
            if (message?.type === 'pong') {
                return; // Heartbeat response, no further processing needed
            }

            // Handle authentication errors - session expired
            if (message?.other_data?.error_type === 'authentication') {
                // TODO: Future optimizations:
                // - Show a toast notification before redirecting
                // - Attempt to refresh the token first before logging out
                // - Store conversation state for restoration after re-login

                console.warn('[QPilot] Authentication error received:', message.other_data.error_code);

                // Dispatch auth-failed event to trigger centralized logout handling in App.js
                window.dispatchEvent(new CustomEvent('auth-failed', {
                    detail: {
                        reason: 'websocket_session_expired',
                        message: message.message,
                        error_code: message.other_data.error_code
                    }
                }));

                return; // Stop processing this message
            }

            // Handle streaming messages
            if (message?.other_data?.stream === true) {
                const questionId = message.other_data.question_id;
                const sequenceId = message.other_data.sequence_id;
                const fragment = message.message || "";

                // Clear any loading state when streaming starts
                if (sequenceId === 0 || sequenceId === 1) {
                    setAiMessage("");
                }

                setMessageHistory((prev) => {
                    // Find existing streaming message with this question_id
                    const existingIndex = prev.findIndex(m =>
                        m.other_data?.question_id === questionId &&
                        m.other_data?.is_streaming === true
                    );

                    if (existingIndex >= 0) {
                        // Update existing streaming message
                        const updated = [...prev];
                        updated[existingIndex] = {
                            ...updated[existingIndex],
                            message: (updated[existingIndex].message || "") + fragment,
                            other_data: {
                                ...updated[existingIndex].other_data,
                                last_sequence_id: sequenceId
                            }
                        };
                        return updated;
                    } else {
                        // Create new streaming message
                        return [...prev, {
                            ...message,
                            message: fragment,
                            other_data: {
                                ...message.other_data,
                                is_streaming: true,
                                last_sequence_id: sequenceId
                            }
                        }];
                    }
                });
                return;
            }

            // Handle initial activation
            if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("Activating QPilot") > -1) {
                setAiMessageWithTimeout("Activating QPilot...");
            }
            // Handle QF STEP messages with replacements
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("QF STEP 1: GATHERING INPUTS // ") > -1) {
                setAiMessageWithTimeout("Gathering inputs...");
            }
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("QF STEP 2: QUESTION AND CONTEXT // ") > -1) {
                setAiMessageWithTimeout("Examining context...");
            }
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("QF STEP 3: SELECTING RESPONSE // ") > -1) {
                setAiMessageWithTimeout("Getting organized...");
            }
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("QF STEP 4: SELECTING TOOL PARAMETERS //") > -1) {
                setAiMessageWithTimeout("Refining parameters...");
            }
            // Handle agent starting - prepare for streaming
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("Starting agent:") > -1) {
                setAiMessageWithTimeout("Starting the engine...");
            }
            // Handle tool starting (case insensitive)
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.toLowerCase().indexOf("starting tool:") > -1) {
                setAiMessageWithTimeout("Querying data...");
            }
            // Handle tool completion (case insensitive)
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.toLowerCase().indexOf("done tool:") > -1) {
                setAiMessageWithTimeout("Synthesizing results...");
            }
            // Handle agent completion - replace "Done Agent:" portion and keep the response
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && message.message.indexOf("Done agent:") > -1) {
                const cleanedMessage = message.message.replace("Done agent: AgentExecutor with output: ", "").replace("Done agent: ", "");
                setAiMessage("")

                // Calculate response time
                const endTime = Date.now();
                const responseTime = responseStartTime ? ((endTime - responseStartTime) / 1000).toFixed(2) : null;

                // If there's a streaming message for this question_id, replace it with the final version
                const questionId = message.other_data?.question_id;

                setMessageHistory((prev) => {
                    if (questionId) {
                        // Find and remove any streaming message with this question_id
                        const filtered = prev.filter(m =>
                            !(m.other_data?.question_id === questionId && m.other_data?.is_streaming === true)
                        );
                        // Add the final message
                        return filtered.concat({
                            ...message,
                            message: cleanedMessage,
                            chart_spec: message.chart_spec,
                            visualization_enabled: message.visualization_enabled,
                            charts: message.other_data?.charts || message.charts,
                            diagnostics: {
                                ...(message.other_data?.diagnostic_data || message.other_data || message.diagnostics || {}),
                                response_time_seconds: responseTime
                            }
                        });
                    } else {
                        // No question_id, add normally
                        return prev.concat({
                            ...message,
                            message: cleanedMessage,
                            chart_spec: message.chart_spec,
                            visualization_enabled: message.visualization_enabled,
                            charts: message.other_data?.charts || message.charts,
                            diagnostics: {
                                ...(message.other_data?.diagnostic_data || message.other_data || message.diagnostics || {}),
                                response_time_seconds: responseTime
                            }
                        });
                    }
                });

                // Stop response timer and reset timeout states
                setIsWaitingForResponse(false);
                setResponseStartTime(null);
                setCurrentStatusStartTime(null);
                setShowSingleStatusTimeout(false);
                setShowTotalTimeout(false);
            }
            // Handle other tool/system messages that should be filtered out
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0 && (
                message.message.toLowerCase().indexOf("tool output was:") > -1 ||
                message.message.indexOf("QF STEP") > -1
            )) {
                // Filter out these system messages
                return
            }
            // Handle actual response content
            else if (message && Object.keys(message).length > 0 && message.message && message.message.length > 0) {
                // Check if we're in a loading state that should block content
                const currentAiMessage = aiMessage;
                const isInBlockingLoadingState = currentAiMessage.includes("Activating QPilot") ||
                                               currentAiMessage.includes("Gathering inputs") ||
                                               currentAiMessage.includes("Examining context") ||
                                               currentAiMessage.includes("Getting organized") ||
                                               currentAiMessage.includes("Starting the engine") ||
                                               currentAiMessage.includes("Refining parameters") ||
                                               currentAiMessage.includes("Starting the engine") ||
                                               currentAiMessage.includes("Querying data");

                // If we're in "Synthesizing results..." state, clear it before starting to stream
                if (currentAiMessage === "Synthesizing results...") {
                    setAiMessage(message.message);
                } else if (!isInBlockingLoadingState) {
                    // Not in any loading state, append normally
                    setAiMessage((prev) => prev + message.message);
                }
            }
        }


    });

    // Helper function to check connection health (moved after useWebSocket)
    const checkConnectionHealth = useCallback(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity;

        if (timeSinceLastActivity > INACTIVITY_TIMEOUT && readyState === ReadyState.OPEN) {
        
            sendJsonMessage({ type: 'ping', timestamp: now });
        }

        // If we haven't received any activity for 3x the heartbeat interval, reconnect
        // BUT don't reconnect if we're actively waiting for a response to avoid "GoneException"
        if (timeSinceLastActivity > HEARTBEAT_INTERVAL * 3 && readyState === ReadyState.OPEN && !isWaitingForResponse) {
            
            // Force reconnection by updating socket URL with timestamp
            setSocketUrl(prev => `${config.websocket.URL}?t=${Date.now()}`);
        }
    }, [lastActivity, readyState, sendJsonMessage, INACTIVITY_TIMEOUT, HEARTBEAT_INTERVAL]);



    // Define the loading-shimmer animation
const loadingShimmer = keyframes`
0% {
    background-position: -100% top;
}
100% {
    background-position: 200% top;
}
`;

    // Track model changes and add divider
    useEffect(() => {
        if (previousChannelRef.current && previousChannelRef.current !== selectedChannel && messageHistory.length > 0) {
            // Add a divider message when switching models
            const modelName = selectedChannel === "chat-stream" ? "chat-stream" : "chat-stream-v2";
            setMessageHistory(prev => [...prev, {
                type: "divider",
                message: `Switched to ${modelName}`,
                timestamp: new Date().toISOString()
            }]);
        }
        previousChannelRef.current = selectedChannel;
    }, [selectedChannel, messageHistory.length]);

    // Auto-create or load session when switching to chat-stream-v2
    useEffect(() => {
        if (selectedChannel === "chat-stream-v2" && !currentSessionId) {
            // If no current session, create a new one
            const activeSessions = sessions.filter(s => s.status === 'active');
            if (activeSessions.length > 0) {
                // Load the most recent active session
                setCurrentSessionId(activeSessions[0].id);
                // Persist to localStorage
                localStorage.setItem('qpilot-current-session-id', activeSessions[0].id);
            } else {
                // Create a new session with UUID
                const newSession = {
                    id: generateUUID(),
                    name: `Chat - ${new Date().toLocaleDateString()}`,
                    lastModified: new Date().toISOString(),
                    status: 'active',
                    preview: '',
                    messageCount: 0
                };
                setSessions(prev => {
                    const updated = [newSession, ...prev];
                    localStorage.setItem('qpilot-sessions', JSON.stringify(updated));
                    return updated;
                });
                setCurrentSessionId(newSession.id);
                localStorage.setItem('qpilot-current-session-id', newSession.id);
            }
        }
    }, [selectedChannel, currentSessionId]);

    // Run when the connection state (readyState) changes
    useEffect(() => {
        const getTokenAndSubscribe = async () => {
            try {
                const token = await getIdJwtToken();

                sendJsonMessage({
                    event: "subscribe",
                    jwt: token,
                    data: {
                        channel: selectedChannel,
                    },
                    keep: true
                });
            } catch (error) {
                console.error("Error retrieving JWT token:", error);
                // Optionally handle authentication errors (e.g., redirect to login)
            }
        };
        getTokenAndSubscribe();
    }, [readyState, selectedChannel])

// // Try to reconnect if the user's auth token changes
//     const previousAuthToken = usePreviousValue(currUser.authToken)
//
//     useEffect(() => {
//         if (readyState === ReadyState.OPEN && previousAuthToken !== currUser.authToken) {
//             // alert(`Alert - auth token changed \n ${previousAuthToken} \n vs \n ${currUser.authToken}`)
//             sendJsonMessage({
//                 event: "subscribe",
//                 jwt: currUser.authToken,
//                 data: {
//                     channel: "chat-stream",
//                 },
//             })
//         }
//     }, [currUser.authToken])
//
//     const clearMessageHistory = () => {
//         setMessageHistory([])
//     }


    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];


    // Prebuilt questions organized by model capabilities
    const prebuiltQuestionsByModel = React.useMemo(() => ({
        "chat-stream": [
            // Questions that don't require chart/visual generation
            "What marketing programs are driving pipeline this quarter?",
            "What are our prospect conversion rates over the last 6 months?",
            "What was our ARR last quarter?",
            "How is churn trending over the last 6 months?",
            "Any data hygiene issues we should be aware of?",
            "How many deals can a fully ramped rep work at one time?",
            "What was our new business win rate last quarter?",
            "What are predicted bookings for the current quarter?",
            `What deals slipped from this ${forecastPeriodPreference === "quarterly" ? "quarter" : "month"} in the last week?`,
            `How much open pipeline remains for the ${forecastPeriodPreference === "quarterly" ? "quarter" : "month"}?`,
            'Who are my top performing sales reps?',
            "Which deals should I be focused on and why?",
            "How are stage conversion rates changing over the last 6 months?"
        ],
        "chat-stream-v2": [
            // All questions including those that can generate charts/visuals
            "Generate a bar chart showing me the last 12 months of ARR by month.",
            "What marketing programs are driving pipeline this quarter?",
            "How are stage conversion rates changing over the last 6 months?",
            "What are our prospect conversion rates over the last 6 months?",
            "What was our ARR last quarter?",
            "Generate a bar chart showing pipeline by stage for this quarter.",
            "How is churn trending over the last 6 months?",
            "Any data hygiene issues we should be aware of?",
            "How many deals can a fully ramped rep work at one time?",
            "Create a line chart showing win rate trends over the last year.",
            "What was our new business win rate last quarter?",
            "What are predicted bookings for the current quarter?",
            `What deals slipped from this ${forecastPeriodPreference === "quarterly" ? "quarter" : "month"} in the last week?`,
            `How much open pipeline remains for the ${forecastPeriodPreference === "quarterly" ? "quarter" : "month"}?`,
            "Generate a visualization of sales velocity by segment.",
            'Who are my top performing sales reps?',
            "Which deals should I be focused on and why?",
            "Show me a chart of our sales stages conversion rates."
        ]
    }), [forecastPeriodPreference])

    // Get questions based on selected channel - memoized to prevent recreating on every render
    const prebuiltQuestions = React.useMemo(() =>
        prebuiltQuestionsByModel[selectedChannel] || prebuiltQuestionsByModel["chat-stream"],
        [selectedChannel]
    );

    // Randomly select 2 questions from the prebuilt list
    const getRandomQuestions = React.useCallback(() => {
        const shuffled = [...prebuiltQuestions].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, props.isMobile ? 2 : 4);
    }, [prebuiltQuestions, props.isMobile]);

    const [activePrebuiltQuestions, setActivePrebuiltQuestions] = React.useState(() => getRandomQuestions())

    // Reload prebuilt questions when channel changes
    React.useEffect(() => {
        const shuffled = [...prebuiltQuestions].sort(() => 0.5 - Math.random());
        setActivePrebuiltQuestions(shuffled.slice(0, props.isMobile ? 2 : 4));
    }, [prebuiltQuestions, props.isMobile]); // Only re-run when questions or mobile state changes

    const handleAdoptQuestion = async (question) => {
        // Prevent sending while waiting for a response
        if (isWaitingForResponse) return;

        const token = await getIdJwtToken();
        const outgoingMessage = {
            sender: {
                sender_id: currUser.id, sender_name: `${currUser.firstName} ${currUser.lastName}`
            }, message: sanitizeUserInput(question),
            jwt: token,
            channel: selectedChannel,
            messageHistoryLength: !messageHistory ? 0 : messageHistory.length,
            // Include sessionId for chat-stream-v2 (Phase 2 preparation)
            ...(selectedChannel === "chat-stream-v2" && currentSessionId && { sessionId: currentSessionId })
        }
        setActivePrebuiltQuestions((prev) => {
            const filtered = prev.filter(item => item !== question);
            // If we have less than 2 questions left, get new random ones
            if (filtered.length < 2) {
                const remainingQuestions = prebuiltQuestions.filter(q => !prev.includes(q) || q === question);
                if (remainingQuestions.length > 0) {
                    const newQuestions = remainingQuestions.sort(() => 0.5 - Math.random()).slice(0, 2 - filtered.length);
                    return [...filtered, ...newQuestions];
                }
            }
            return filtered;
        })
        sendJsonMessage({...outgoingMessage}, true)
        setMessageHistory((prev) => prev.concat({...outgoingMessage}));

        // Force scroll to bottom when user sends a message
        setIsNearBottom(true); // Ensure auto-scroll is active
        setTimeout(() => scrollToBottom(true), 100);

        // Start response timer and reset timeout states
        setIsWaitingForResponse(true);
        setResponseStartTime(Date.now());
        setCurrentStatusStartTime(Date.now());
        setElapsedSeconds(0);
        setShowSingleStatusTimeout(false);
        setShowTotalTimeout(false);

        setCurrentPrompt("")
    }


    const handleClearConversation = async () => {
        const token = await getIdJwtToken();
        const outgoingMessage = {
            sender: {
                sender_id: currUser.id, sender_name: `${currUser.firstName} ${currUser.lastName}`
            }, message: "[CLEAR_CONVERSATION]",
            channel: selectedChannel,
            jwt: token,
            // Include sessionId for chat-stream-v2
            ...(selectedChannel === "chat-stream-v2" && currentSessionId && { sessionId: currentSessionId })
        }
        sendJsonMessage({...outgoingMessage}, true)
        setMessageHistory([{
            type: "divider",
            message: `Using ${selectedChannel}`,
            timestamp: new Date().toISOString()
        }]);
        setCurrentPrompt("")
        setAiMessage("")

        // Reset response timer and timeout states
        setIsWaitingForResponse(false);
        setResponseStartTime(null);
        setCurrentStatusStartTime(null);
        setElapsedSeconds(0);
        setShowSingleStatusTimeout(false);
        setShowTotalTimeout(false);
    }

    // Session management functions (Phase 1)
    const handleCreateNewSession = async () => {
        if (selectedChannel !== "chat-stream-v2") return;

        // Create a new session with proper UUID
        const newSession = {
            id: generateUUID(),
            name: `Chat - ${new Date().toLocaleDateString()}`,
            lastModified: new Date().toISOString(),
            status: 'active',
            preview: '',
            messageCount: 0
        };

        setSessions(prev => {
            const updated = [newSession, ...prev];
            // Persist to localStorage
            localStorage.setItem('qpilot-sessions', JSON.stringify(updated));
            return updated;
        });
        setCurrentSessionId(newSession.id);
        // Persist current session ID
        localStorage.setItem('qpilot-current-session-id', newSession.id);
        handleClearConversation();
        setShowSessionManager(false);
    };

    const handleLoadSession = async (sessionId) => {
        if (selectedChannel !== "chat-stream-v2") return;

        // Switch session ID and clear messages
        setCurrentSessionId(sessionId);
        // Persist current session ID
        localStorage.setItem('qpilot-current-session-id', sessionId);
        handleClearConversation();
        setShowSessionManager(false);

        // In Phase 2, this will load messages from the server
    };

    const handleArchiveSession = async (sessionId) => {
        if (selectedChannel !== "chat-stream-v2") return;

        setSessions(prev => {
            const updated = prev.map(session =>
                session.id === sessionId
                    ? { ...session, status: session.status === 'archived' ? 'active' : 'archived' }
                    : session
            );
            // Persist to localStorage
            localStorage.setItem('qpilot-sessions', JSON.stringify(updated));
            return updated;
        });
    };

    const handleDeleteSession = async (sessionId) => {
        if (selectedChannel !== "chat-stream-v2") return;

        // Remove from local state and localStorage
        setSessions(prev => {
            const updated = prev.filter(session => session.id !== sessionId);
            // Persist to localStorage
            localStorage.setItem('qpilot-sessions', JSON.stringify(updated));
            return updated;
        });

        // If deleting current session, switch to a new one
        if (currentSessionId === sessionId) {
            handleCreateNewSession();
        }
    };

    const handleRenameSession = async (sessionId, newName) => {
        if (selectedChannel !== "chat-stream-v2") return;

        setSessions(prev => {
            const updated = prev.map(session =>
                session.id === sessionId
                    ? { ...session, name: newName }
                    : session
            );
            // Persist to localStorage
            localStorage.setItem('qpilot-sessions', JSON.stringify(updated));
            return updated;
        });
    };

    // Filter sessions based on search and status
    const filteredSessions = sessions.filter(session => {
        const matchesSearch = session.name.toLowerCase().includes(sessionSearchQuery.toLowerCase()) ||
                            session.preview.toLowerCase().includes(sessionSearchQuery.toLowerCase());
        const matchesFilter = sessionFilter === 'all' || session.status === sessionFilter;
        return matchesSearch && matchesFilter;
    });

    // File upload functionality
    const fileUploads = useFileUploads();
    const dragAndDrop = useDragAndDrop(fileUploads.addFiles);

    const handleClickSendMessage = useCallback(() => {
        const getJwtAndSend = async () => {
            const token = await getIdJwtToken();
            const outgoingMessage = {
                sender: {
                    sender_id: currUser.id, sender_name: `${currUser.firstName} ${currUser.lastName}`
                },
                message: sanitizeUserInput(currentPrompt || ""),
                channel: selectedChannel,
                messageHistoryLength: !messageHistory ? 0 : messageHistory.length,
                jwt: token,
                attachments: fileUploads.getAttachmentMeta(),
                // Include sessionId for chat-stream-v2 (Phase 2 preparation)
                ...(selectedChannel === "chat-stream-v2" && currentSessionId && { sessionId: currentSessionId })
            }
            sendJsonMessage({...outgoingMessage}, true)
            setMessageHistory((prev) => prev.concat({...outgoingMessage}));
            fileUploads.reset();

            // Force scroll to bottom when user sends a message
            setTimeout(() => scrollToBottom(true), 100);

            // Start response timer
            setIsWaitingForResponse(true);
            setResponseStartTime(Date.now());
            setElapsedSeconds(0);
        }
        if (readyState === ReadyState.OPEN) {
            getJwtAndSend()
        } 
        setCurrentPrompt("")
    }, [currentPrompt, fileUploads, selectedChannel, currentSessionId]);

    // Handle initial question when QPilot opens (moved here after variable declarations)
    useEffect(() => {
        if (props.initialQuestion && props.initialQuestion.length > 0 &&  props.initialQuestion.trim()) {
            // Set the question as the current prompt for display
            setCurrentPrompt(props.initialQuestion);

            // Clear the initial question to prevent re-use
            if (props.clearInitialQuestion) {
                props.clearInitialQuestion();
            }

            // Auto-send the initial question using the props value directly
            const sendInitialQuestion = async () => {
                if (readyState === ReadyState.OPEN) {
                    const token = await getIdJwtToken();
                    const outgoingMessage = {
                        sender: {
                            sender_id: currUser.id, sender_name: `${currUser.firstName} ${currUser.lastName}`
                        },
                        message: sanitizeUserInput(props.initialQuestion), // Use props directly
                        channel: selectedChannel,
                        messageHistoryLength: !messageHistory ? 0 : messageHistory.length,
                        jwt: token,
                        attachments: fileUploads.getAttachmentMeta(),
                        ...(selectedChannel === "chat-stream-v2" && currentSessionId && { sessionId: currentSessionId })
                    };
                    sendJsonMessage({...outgoingMessage}, true);
                    setMessageHistory((prev) => prev.concat({...outgoingMessage}));
                    fileUploads.reset();

                    // Force scroll to bottom and start response timer
                    setTimeout(() => scrollToBottom(true), 100);
                    setIsWaitingForResponse(true);
                    setResponseStartTime(Date.now());
                    setElapsedSeconds(0);
                    setCurrentPrompt(""); // Clear the input after sending
                    props.clearInitialQuestion()
                }
            };

            setTimeout(sendInitialQuestion, 150);

            // Focus on the input field (desktop only) after a short delay for DOM readiness
            if (!props.isMobile) {
                setTimeout(() => {
                    const inputElement = document.getElementById('qpilot_active_chat');
                    if (inputElement) {
                        inputElement.focus();
                        // Move cursor to end of text
                        const textLength = inputElement.value.length;
                        inputElement.setSelectionRange(textLength, textLength);
                    }
                }, 200); // Slightly longer delay since we're clearing the input
            }
        }
    }, [props.showQPilotChat, props.initialQuestion, props.clearInitialQuestion, readyState, selectedChannel]);

    // WEBSOCKET LOGIC END


    


    return (
        <>
            {/* QPilot Avatar toggle button, fixed to right edge, only when sidebar is closed */}
            {!props.showQPilotChat && (
                <Box sx={{
                    position: 'fixed',
                    top: 76, // adjust as needed
                    right: 1,
                    zIndex: 1201,
                }}>
                    <QTooltip placement="left" title="Show QPilot">
                        <ToggleButton
                            size={"small"}
                            id={"qpilot_chatbot_toggle"}
                            value={"open"}
                            sx={{padding: 0, minWidth: 0, width: 28, border: 'none', boxShadow: 'none'}}
                            onClick={() => props.handleShowQPilotChatChange()}
                        >
                            <Avatar
                                variant={"rounded"}
                                size={"small"}
                                src={QBearMascot}
                                sx={{color: "#1de9b6", backgroundColor: "transparent", width: 28, height: 28}}
                            />
                        </ToggleButton>
                    </QTooltip>
                </Box>
            )}

            {/* Focus mode backdrop - only on desktop */}
            {!props.isMobile && props.isFocusMode && props.showQPilotChat && (
                <Box
                    onClick={() => {
                        lastManualToggleRef.current = Date.now();
                        // Close QPilot entirely instead of just exiting focus mode
                        props.handleShowQPilotChatChange();
                    }}
                    role="button"
                    aria-label="Close QPilot"
                    sx={{
                        position: 'fixed',
                        top: 43, // Start below navbar
                        left: props.drawerWidth || 0,
                        bottom: 0,
                        right: 'clamp(640px, 90vw, calc(100vw - 120px))', // Exclude the drawer width area
                        backgroundColor: 'rgba(0,0,0,0.15)',
                        zIndex: 1299,
                        cursor: 'pointer'
                    }}
                />
            )}

            <Drawer
                open={props.showQPilotChat}
                variant={"permanent"}
                anchor={props.isMobile ? "left" : "right"}
                sx={{
                    zIndex: props.isFocusMode ? 1300 : 1200,
                    width: props.qPilotDrawerWidth,
                    flexShrink: 0,
                    // Border depends on anchor position
                    borderLeft: !props.isMobile ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
                    borderRight: props.isMobile ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
                    // Desktop: position below navbar, respect width, avoid border
                    ...(!props.isMobile && {
                        '& .MuiDrawer-paper': {
                            top: 43, // Start just below the navbar border
                            height: 'calc(100vh - 43px)',
                            width: !props.showQPilotChat
                                ? '32px'  // Collapsed when closed, regardless of focus mode
                                : props.isFocusMode
                                    ? 'clamp(640px, 90vw, calc(100vw - 120px))'
                                    : props.qPilotDrawerWidth, // Focus mode width or normal width
                            transition: 'width 300ms ease',
                        }
                    }),
                    // Mobile: full screen above navbar
                    ...(props.isMobile && props.showQPilotChat && {
                        position: 'fixed',
                        top: 42,
                        left: 0,
                        right: 0,
                        width: '100%',
                        height: 'calc(100vh - 42px - env(safe-area-inset-bottom))', // Account for iOS safe area
                        zIndex: 1300,
                        '& .MuiDrawer-paper': {
                            position: 'fixed',
                            top: 42,
                            left: 0,
                            right: 0,
                            width: '100%',
                            height: 'calc(100vh - 42px - env(safe-area-inset-bottom))',
                            boxSizing: 'border-box'
                        }
                    }),
                }}
            >

                <Box sx={{
                    // pt: "70px", // keep this for spacing below the button
                    height: props.isMobile
                        ? '100%'
                        : `calc(100vh - 70px)`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    position: "relative",
                    overflow: "hidden", // Prevent background scrolling issues
                    width: "100%",
                    boxSizing: "border-box",
                    ...(props.isMobile && {
                        touchAction: "pan-y", // Allow vertical scrolling but not horizontal
                        maxWidth: "100vw"
                    })
                }}>
                    {props.showQPilotChat &&
                        <Box sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column"
                        }}>
                            {/* Universal QPilot Header - Pinned to top */}
                            <Box sx={{
                                borderBottom: "1px solid rgba(0,0,0,0.1)",
                                backgroundColor: "background.paper",
                                px: 2,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                height: "40px",
                                justifyContent: "space-between",
                                flexShrink: 0,
                                position: "sticky",
                                top: 0,
                                zIndex: 10
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            color: "text.primary"
                                        }}
                                    >
                                        QPilot - Using {selectedChannel}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {!props.isMobile && (
                                        <Tooltip title={props.isFocusMode ? "Exit focus mode" : "Enter focus mode"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    lastManualToggleRef.current = Date.now();
                                                    props.onToggleFocus();
                                                }}
                                                sx={{
                                                    p: 0.5,
                                                    color: "text.secondary",
                                                    "&:hover": { color: "primary.main" }
                                                }}
                                            >
                                                {props.isFocusMode ? <FullscreenExitIcon fontSize="small" /> : <ZoomOutMapIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Close QPilot">
                                        <IconButton
                                            size="small"
                                            onClick={() => props.handleShowQPilotChatChange()}
                                            sx={{ 
                                                p: 0.5,
                                                color: "text.secondary",
                                                "&:hover": { color: "primary.main" }
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            {/* Session Management Header - Only visible in chat-stream-v2 mode */}
                            {selectedChannel === "chat-stream-v2" && (
                                <Box sx={{
                                    borderBottom: "1px solid rgba(0,0,0,0.1)",
                                    backgroundColor: "background.paper",
                                    px: 2,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    height: "40px",
                                    justifyContent: "space-between",
                                    flexShrink: 0
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                        <ChatIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                flex: 1
                                            }}
                                        >
                                            {sessions.find(s => s.id === currentSessionId)?.name || "New Chat"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <Tooltip title="New Chat">
                                            <IconButton
                                                size="small"
                                                onClick={handleCreateNewSession}
                                                sx={{
                                                    color: "text.secondary",
                                                    '&:hover': { backgroundColor: "action.hover" }
                                                }}
                                            >
                                                <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Chat History">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowSessionManager(true)}
                                                sx={{
                                                    color: "text.secondary",
                                                    '&:hover': { backgroundColor: "action.hover" }
                                                }}
                                            >
                                                <HistoryIcon fontSize="small" sx={{ color: "primary.main" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}



                            {props.isInitializing && <Grid container spacing={3} alignItems={"center"}
                                                   justifyContent={"center"}><Grid item xs={12} lg={12}>
                                <Typography sx={{textAlign: "center"}}>
                                    Initializing QPilot...
                                </Typography>
                            </Grid></Grid>}

                            <Alert
                                icon={false}
                                sx={{
                                    ".MuiAlert-message": {
                                        minWidth: "100%",
                                        width: "100%",
                                        padding: 0
                                    },
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    boxSizing: "border-box",
                                    ...(props.isMobile && {
                                        minHeight: 0, // Allow proper shrinking on mobile
                                        maxWidth: "100%"
                                    })
                                }}
                                style={{
                                    backgroundColor: "rgb(249, 249, 249)",
                                    color: "#000000",
                                    height: "100%",
                                    width: "100%",
                                    padding: "0px",
                                    flexShrink: 0,
                                    boxSizing: "border-box",
                                    ...(props.isMobile && {
                                        WebkitTransform: "translateZ(0)", // Hardware acceleration
                                        transform: "translateZ(0)"
                                    })
                                }}
                                severity={"success"}>
                                <div
                                    ref={props.showQPilotChat ? messageContainerRef : null}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        position: "relative",
                                        scrollBehavior: "smooth",
                                        // Use native momentum scrolling on iOS
                                        WebkitOverflowScrolling: "touch",
                                        // Prevent pull-to-refresh on mobile
                                        overscrollBehavior: "contain"
                                    }}>
                                    <List sx={{width: '100%'}}>
                                        {messageHistory.map((item, i) => {
                                            // Check if this is a divider message
                                            if (item.type === "divider") {
                                                return (
                                                    <ListItem key={"divider" + i} sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        py: 2,
                                                        px: 2
                                                    }}>
                                                        <Box sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            width: "100%",
                                                            maxWidth: "400px"
                                                        }}>
                                                            <Divider sx={{ flex: 1 }} />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    px: 2,
                                                                    color: 'text.secondary',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {item.message}
                                                            </Typography>
                                                            <Divider sx={{ flex: 1 }} />
                                                        </Box>
                                                    </ListItem>
                                                );
                                            }

                                            // Regular message rendering
                                            return (
                                            <ListItem key={"chat" + i} style={{
                                                width: "100%",
                                                display: "inline-flex",
                                                justifyContent: item.sender['sender_id'] === "qpilot" ? "flex-start" : "flex-end",
                                                alignItems: item.sender['sender_id'] === "qpilot" ? "flex-start" : "flex-end",
                                                paddingTop: props.isMobile ? "6px" : "8px",
                                                paddingBottom: props.isMobile ? "6px" : "8px",
                                                paddingLeft: props.isMobile ? "12px" : "16px",
                                                paddingRight: props.isMobile ? "12px" : "16px",
                                            }}>
                                                {/*        <Grid container*/}
                                                {/*              justifyContent={item.sender['sender_id'] === "qpilot" ? "flex-start" : "flex-end"}*/}
                                                {/*         style={{*/}
                                                {/*    textAlign: item.sender['sender_id'] === "qpilot" ? "left" : "right",*/}
                                                {/*    width: "100%",*/}
                                                {/*}}>*/}
                                                <Stack spacing={1}
                                                       direction={item.sender['sender_id'] === "qpilot" ? "row" : "row-reverse"}>
                                                    {item.sender['sender_id'] === "qpilot" &&
                                                        <ListItemAvatar sx={{p: 0, m: 0, minWidth: 34}}>
                                                            <Avatar
                                                                variant={"rounded"}
                                                                size={"small"}
                                                                src={item.sender['sender_id'] === "qpilot" ? QBearMascot : undefined}
                                                                sx={{
                                                                    color: "#FFFFFF",
                                                                    backgroundColor: "#272931",
                                                                    width: 28,
                                                                    height: 28,
                                                                }}
                                                            >{props.currUser.firstName.slice(0, 1) + props.currUser.lastName.slice(0, 1)}</Avatar>
                                                        </ListItemAvatar>
                                                    }
                                                    <div style={{
                                                        paddingTop: props.isMobile ? "10px" : "4px",
                                                        paddingBottom: props.isMobile ? "10px" : "4px",
                                                        paddingLeft: props.isMobile ? "14px" : "8px",
                                                        paddingRight: props.isMobile ? "14px" : "8px",
                                                        minWidth: props.isMobile ? "60px" : "80px",
                                                        maxWidth: props.isMobile ? "85%" : "95%",
                                                        border: item.sender['sender_id'] === "qpilot" ? "0px #b6b6b6 solid" : "1px #4DA685 solid",
                                                        borderRadius: item.sender['sender_id'] === "qpilot" ? (props.isMobile ? "20px" : "8px") : (props.isMobile ? "20px" : "8px"),
                                                        color: item.sender['sender_id'] === "qpilot" ? undefined : "#FFFFFF",
                                                        backgroundColor: item.sender['sender_id'] === "qpilot" ? (props.isMobile ? "#f0f0f0" : undefined) : "#4DA685",
                                                        fontSize: props.isMobile ? "15px" : "14px",
                                                        lineHeight: props.isMobile ? "1.4" : "1.5",
                                                        wordWrap: "normal",
                                                        overflowWrap: "normal",
                                                        display: "inline-block",
                                                        boxSizing: "border-box"
                                                    }}>
                                                        {/* Attachments Display */}
                                                        {item.attachments && item.attachments.length > 0 && (
                                                            <Box sx={{ mb: 1 }}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                                                                    Attachments:
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {item.attachments.map((attachment, index) => (
                                                                        <Chip
                                                                            key={index}
                                                                            label={attachment.name}
                                                                            size="small"
                                                                            clickable
                                                                            onClick={() => window.open(attachment.url, '_blank')}
                                                                            sx={{
                                                                                backgroundColor: item.sender['sender_id'] === "qpilot" ? 'primary.main' : 'rgba(255,255,255,0.2)',
                                                                                color: item.sender['sender_id'] === "qpilot" ? 'white' : 'inherit',
                                                                                fontSize: '0.7rem',
                                                                                height: 20
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        <Markdown
                                                        remarkPlugins={[remarkGfm, [remarkMath, remarkMathOptions]]}
                                                        rehypePlugins={[rehypeKatex]}
                                                        components={components}
                                                        className={"QPilotStyles"}
                                                    >{item.message}</Markdown>
                                                    {/* Chart Display for QPilot messages - supports both old single chart_spec and new charts array */}
                                                    {item.sender['sender_id'] === "qpilot" && item.chart_spec && item.visualization_enabled && (
                                                        <Box sx={{ mt: props.isFocusMode ? 2 : 1, mb: props.isFocusMode ? 1 : 0.5 }}>
                                                            <QPilotChart
                                                                spec={{
                                                                    ...item.chart_spec,
                                                                    width: props.isFocusMode
                                                                        ? Math.min(Math.max(640, window.innerWidth * 0.8), 1040) - 180
                                                                        : (qPilotDrawerWidth === "100%" ? window.innerWidth - 80 : Math.max(160, qPilotDrawerWidth - 200)),
                                                                    height: props.isFocusMode ? (item.chart_spec.height || 400) : 120,
                                                                    title: props.isFocusMode ? item.chart_spec.title : (item.chart_spec.title && item.chart_spec.title.length > 30 ? null : item.chart_spec.title),
                                                                    padding: props.isFocusMode ? { left: 40, right: 40, top: 20, bottom: 20 } : (item.chart_spec.padding || 5),
                                                                    mark: item.chart_spec.mark ? {
                                                                        ...item.chart_spec.mark,
                                                                        ...(item.chart_spec.mark.type === 'bar' && {
                                                                            size: props.isFocusMode ? 30 : 25
                                                                        })
                                                                    } : item.chart_spec.mark,
                                                                    encoding: item.chart_spec.encoding ? {
                                                                        ...item.chart_spec.encoding,
                                                                        x: item.chart_spec.encoding.x ? {
                                                                            ...item.chart_spec.encoding.x,
                                                                            axis: props.isFocusMode ? item.chart_spec.encoding.x.axis : { labels: false, title: null }
                                                                        } : item.chart_spec.encoding.x,
                                                                        y: item.chart_spec.encoding.y ? {
                                                                            ...item.chart_spec.encoding.y,
                                                                            axis: props.isFocusMode ? item.chart_spec.encoding.y.axis : { labels: false, title: null }
                                                                        } : item.chart_spec.encoding.y
                                                                    } : item.chart_spec.encoding
                                                                }}
                                                                theme={props.theme || 'light'}
                                                                isMobile={props.isMobile}
                                                                isFocusMode={props.isFocusMode}
                                                                onWidthOverflow={handleAutoFocus}
                                                            />
                                                        </Box>
                                                    )}
                                                    {/* New format: Charts array from other_data.charts */}
                                                    {item.sender['sender_id'] === "qpilot" && item.charts && item.charts.length > 0 && (
                                                        <Box sx={{ mt: props.isFocusMode ? 2 : 1, mb: props.isFocusMode ? 1 : 0.5 }}>
                                                            {item.charts.map((chartSpec, index) => (
                                                                <Box key={`chart-${i}-${index}`} sx={{ mb: index < item.charts.length - 1 ? (props.isFocusMode ? 2 : 1) : 0 }}>
                                                                    <QPilotChart
                                                                        spec={{
                                                                            ...chartSpec,
                                                                            width: props.isFocusMode
                                                                                ? Math.min(Math.max(640, window.innerWidth * 0.8), 1040) - 180
                                                                                : (qPilotDrawerWidth === "100%" ? window.innerWidth - 80 : Math.max(160, qPilotDrawerWidth - 200)),
                                                                            height: props.isFocusMode ? (chartSpec.height || 400) : 120,
                                                                            title: props.isFocusMode ? chartSpec.title : (chartSpec.title && chartSpec.title.length > 30 ? null : chartSpec.title),
                                                                            padding: props.isFocusMode ? { left: 40, right: 40, top: 20, bottom: 20 } : (chartSpec.padding || 5),
                                                                            mark: chartSpec.mark ? {
                                                                                ...chartSpec.mark,
                                                                                ...(chartSpec.mark.type === 'bar' && {
                                                                                    size: props.isFocusMode ? 30 : 25
                                                                                })
                                                                            } : chartSpec.mark,
                                                                            encoding: chartSpec.encoding ? {
                                                                                ...chartSpec.encoding,
                                                                                x: chartSpec.encoding.x ? {
                                                                                    ...chartSpec.encoding.x,
                                                                                    axis: props.isFocusMode ? chartSpec.encoding.x.axis : { labels: false, title: null }
                                                                                } : chartSpec.encoding.x,
                                                                                y: chartSpec.encoding.y ? {
                                                                                    ...chartSpec.encoding.y,
                                                                                    axis: props.isFocusMode ? chartSpec.encoding.y.axis : { labels: false, title: null }
                                                                                } : chartSpec.encoding.y
                                                                            } : chartSpec.encoding
                                                                        }}
                                                                        theme={props.theme || 'light'}
                                                                        isMobile={props.isMobile}
                                                                        isFocusMode={props.isFocusMode}
                                                                        onWidthOverflow={handleAutoFocus}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                    {/* Diagnostic chips for QPilot messages in test environment */}
                                                    {item.sender['sender_id'] === "qpilot" && item.diagnostics && (
                                                        <DiagnosticChips diagnostics={item.diagnostics} />
                                                    )}
                                                    </div>
                                                </Stack>
                                                {/*</Grid>*/}
                                            </ListItem>
                                            );
                                        })}

                                        
                                    </List>

                                    {aiMessage &&
                                        <ListItem
                                            key={"incoming_message"}
                                            sx={{
                                                alignItems: "flex-start",
                                                paddingTop: props.isMobile ? "4px" : "8px",
                                                paddingBottom: props.isMobile ? "4px" : "8px",
                                                paddingLeft: props.isMobile ? "8px" : "16px",
                                                paddingRight: props.isMobile ? "8px" : "16px",
                                            }}
                                        >
                                            <Stack spacing={1} direction={"row"}>
                                                <ListItemAvatar sx={{p: 0, m: 0, minWidth: 34}}>
                                                    <Avatar
                                                        variant={"rounded"}
                                                        size={"small"}
                                                        src={QBearMascot}
                                                        sx={{
                                                            color: "#1de9b6",
                                                            backgroundColor: "#272931",
                                                            width: 28,
                                                            height: 28,
                                                        }}
                                                    />
                                                </ListItemAvatar>

                                                <div style={{
                                                    paddingTop: props.isMobile ? "10px" : "4px",
                                                    paddingBottom: props.isMobile ? "10px" : "4px",
                                                    paddingLeft: props.isMobile ? "14px" : "8px",
                                                    paddingRight: props.isMobile ? "14px" : "8px",
                                                    minWidth: props.isMobile ? "60px" : "80px",
                                                    maxWidth: props.isMobile ? "85%" : "75%",
                                                    border: "0px #b6b6b6 solid",
                                                    borderRadius: props.isMobile ? "20px" : "8px",
                                                    color: undefined,
                                                    backgroundColor: props.isMobile ? "#f0f0f0" : undefined,
                                                    fontSize: props.isMobile ? "15px" : "14px",
                                                    lineHeight: props.isMobile ? "1.4" : "1.5",
                                                    wordWrap: "normal",
                                                    overflowWrap: "break-word",
                                                    display: "inline-block",
                                                    boxSizing: "border-box"
                                                }}>

                                                    {aiMessage && (
                                                        aiMessage === "Activating QPilot..." ||
                                                        aiMessage === "Gathering inputs..." ||
                                                        aiMessage === "Examining context..." ||
                                                        aiMessage === "Getting organized..." ||
                                                        aiMessage === "Querying data..." ||
                                                        aiMessage === "Refining parameters..." ||
                                                        aiMessage === "Starting the engine..." ||
                                                        aiMessage === "Synthesizing results..."
                                                    ) ?
                                                     <div>
                                                         <Typography
                                                         sx={{
                                                             '--text-quaternary': '#272931',
                                                             '--text-primary': '#4DA685',
                                                             textFillColor: 'transparent',
                                                             fontWeight: 600,
                                                             WebkitTextFillColor: 'transparent',
                                                             animation: `${loadingShimmer} 3s infinite`,
                                                             animationDelay: '0.5s',
                                                             background: `var(--text-quaternary) linear-gradient(
                                                                 90deg,
                                                                 var(--text-quaternary) 0%,
                                                                 var(--text-primary) 50%,
                                                                 var(--text-quaternary) 100%
                                                             )`,
                                                             backgroundClip: 'text',
                                                             WebkitBackgroundClip: 'text',
                                                             backgroundRepeat: 'no-repeat',
                                                             backgroundSize: '50% 200%',
                                                             backgroundPosition: '-100% top',
                                                             display: 'inline-block',
                                                             direction: 'ltr',
                                                             whiteSpace: 'nowrap',
                                                             // Optional: add smooth transition for text changes
                                                             transition: 'opacity 0.3s ease-in-out',
                                                         }}
                                                     >
                                                         {aiMessage}
                                                     </Typography>
                                                     {isWaitingForResponse && (
                                                         <Typography
                                                             variant="caption"
                                                             sx={{
                                                                 display: 'block',
                                                                 color: '#666',
                                                                 fontSize: '11px',
                                                                 fontWeight: 400,
                                                                 marginTop: '4px',
                                                                 opacity: 0.7
                                                             }}
                                                         >
                                                             {elapsedSeconds}s
                                                         </Typography>
                                                     )}
                                                     {showSingleStatusTimeout && (
                                                         <Typography
                                                             variant="caption"
                                                             sx={{
                                                                 display: 'block',
                                                                 color: '#e65100',
                                                                 fontSize: '11px',
                                                                 fontWeight: 600,
                                                                 marginTop: '6px',
                                                                 backgroundColor: 'rgba(255, 152, 0, 0.15)',
                                                                 padding: '4px 8px',
                                                                 borderRadius: '4px',
                                                                 border: '1px solid rgba(255, 152, 0, 0.4)',
                                                                 whiteSpace: 'nowrap'
                                                             }}
                                                         >
                                                             Taking longer than usual...
                                                         </Typography>
                                                     )}
                                                     {showTotalTimeout && (
                                                         <Typography
                                                             variant="caption"
                                                             sx={{
                                                                 display: 'block',
                                                                 color: '#c62828',
                                                                 fontSize: '11px',
                                                                 fontWeight: 600,
                                                                 marginTop: '6px',
                                                                 backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                                                 padding: '4px 8px',
                                                                 borderRadius: '4px',
                                                                 border: '1px solid rgba(244, 67, 54, 0.4)',
                                                                 cursor: 'pointer',
                                                                 whiteSpace: 'nowrap',
                                                                 '&:hover': {
                                                                     backgroundColor: 'rgba(244, 67, 54, 0.2)'
                                                                 }
                                                             }}
                                                             onClick={() => window.location.reload()}
                                                         >
                                                             Timeout. Click to refresh.
                                                         </Typography>
                                                     )}
                                                     </div>
                                                    : <Markdown
                                                    remarkPlugins={[remarkGfm, [remarkMath, remarkMathOptions]]}
                                                    rehypePlugins={[rehypeKatex]}
                                                    components={components}
                                                    className={"QPilotStyles"}
                                                >{aiMessage}</Markdown>}
                                                </div>
                                            </Stack>
                                        </ListItem>}
                                    <div ref={messagesEndRef} />
                                </div>



                            </Alert>
                            {/* ComposerShell - Input area with file attachments */}
                            <Box
                                sx={{
                                    position: "sticky",
                                    bottom: 0,
                                    backgroundColor: "rgb(249, 249, 249)",
                                    paddingTop: props.isMobile ? "0.5em" : "0.75em",
                                    paddingBottom: props.isMobile ? "env(safe-area-inset-bottom, 0.5em)" : "0.75em",
                                    paddingLeft: props.isMobile ? "0.5em" : "0.75em",
                                    paddingRight: props.isMobile ? "0.5em" : "0.75em",
                                    borderTop: "1px solid rgba(0,0,0,0.05)", // Subtle separation
                                    flexShrink: 0, // Prevent input area from shrinking
                                    overflow: "hidden", // Prevent any scrollbars in input area
                                    boxSizing: "border-box", // Ensure padding is included in width
                                    width: "100%" // Explicit width to prevent overflow
                                }}
                                {...dragAndDrop.dragProps}
                            >
                                <Grid container spacing={props.isMobile ? 0.5 : 1} justifyContent={"center"}>
                                    {/* Prebuilt Questions */}
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            pb: props.isMobile ? 0.25 : 1,
                                            overflowX: props.isMobile ? "auto" : "visible",
                                            overflowY: "hidden",
                                            WebkitOverflowScrolling: "touch",
                                            scrollbarWidth: "none", // Hide scrollbar on Firefox
                                            "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar on WebKit
                                            mx: props.isMobile ? -0.75 : 0, // Negative margin to allow edge-to-edge scroll
                                            px: props.isMobile ? 0.75 : 0 // Padding to restore content position
                                        }}>
                                            <Box sx={{
                                                display: "flex",
                                                gap: props.isMobile ? 0.5 : 1,
                                                flexWrap: props.isMobile ? "nowrap" : "wrap"
                                            }}>
                                                {activePrebuiltQuestions.map((item) => <Chip
                                                sx={{
                                                    cursor: "pointer",
                                                    fontSize: props.isMobile ? '0.75rem' : '0.8125rem',
                                                    height: props.isMobile ? 32 : 32, // Increased touch target
                                                    minWidth: "fit-content",
                                                    flexShrink: 0, // Prevent chips from shrinking on mobile
                                                    '& .MuiChip-label': {
                                                        paddingLeft: props.isMobile ? '10px' : '12px',
                                                        paddingRight: props.isMobile ? '10px' : '12px',
                                                        whiteSpace: "nowrap" // Prevent text wrapping
                                                    }
                                                }}
                                                key={item}
                                                label={item}
                                                onClick={() => handleAdoptQuestion(item)}
                                            />)}
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Attachment Tray */}
                                    <Grid item xs={12}>
                                        <AttachmentTray
                                            files={fileUploads.files}
                                            onRemove={fileUploads.removeFile}
                                            onRetry={fileUploads.retryUpload}
                                            onReorder={fileUploads.reorderFiles}
                                        />
                                    </Grid>

                                    {/* Text Input Area with integrated controls */}
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            position: 'relative',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}>
                                            <TextField
                                                sx={{
                                                    fontSize: 12,
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    '& .MuiInputBase-root': {
                                                        paddingBottom: props.isMobile ? '36px' : '36px', // Space for button row
                                                        paddingTop: props.isMobile ? '10px' : '12px',
                                                        paddingLeft: props.isMobile ? '12px' : '12px',
                                                        paddingRight: props.isMobile ? '12px' : '12px',
                                                        borderRadius: props.isMobile ? '20px' : '8px', // More native feel on mobile
                                                        minHeight: props.isMobile ? '52px' : 'auto' // Better touch target
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: props.isMobile ? '16px' : '16px', // Prevent zoom on iOS
                                                        lineHeight: props.isMobile ? '1.3' : '1.4',
                                                    }
                                                }}
                                                onChange={handleCurrentPrompt}
                                                id={"qpilot_active_chat"}
                                                size={"small"}
                                                maxRows={props.isMobile ? 1 : 3}
                                                rows={props.isMobile ? 1 : 3}
                                                autoFocus={!props.isMobile} // Disable autofocus on mobile to prevent keyboard popup
                                                multiline
                                                InputProps={{
                                                    style: { resize: 'none' }
                                                }}
                                                placeholder={"Message QPilot..."}
                                                value={currentPrompt}
                                            />
                                            {/* Button row inside the text field */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: props.isMobile ? 6 : 8,
                                                    left: props.isMobile ? 12 : 12,
                                                    right: props.isMobile ? 12 : 12,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    pointerEvents: 'none', // Allow clicks to pass through to buttons
                                                    '& > *': {
                                                        pointerEvents: 'auto' // Re-enable clicks on child elements
                                                    }
                                                }}
                                            >
                                                <AttachButton
                                                    onFilesSelected={fileUploads.addFiles}
                                                    filesCount={fileUploads.files.length}
                                                    disabled={fileUploads.hasUploading || attachmentsDisabled}
                                                />
                                                <IconButton
                                                    onClick={handleClickSendMessage}
                                                    disabled={isWaitingForResponse || ((!currentPrompt || !currentPrompt.trim()) && fileUploads.files.length === 0) || fileUploads.hasUploading || (fileUploads.files.length > 0 && !fileUploads.allUploaded)}
                                                    size={props.isMobile ? "small" : "small"}
                                                    sx={{
                                                        backgroundColor: 'primary.main',
                                                        color: 'white',
                                                        width: props.isMobile ? 28 : 32,
                                                        height: props.isMobile ? 28 : 32,
                                                        minWidth: props.isMobile ? 28 : 32,
                                                        flexShrink: 0,
                                                        '&:hover': {
                                                            backgroundColor: 'primary.dark'
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: 'grey.300',
                                                            color: 'grey.500'
                                                        }
                                                    }}
                                                >
                                                    <FiArrowUp size={props.isMobile ? 12 : 16} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Bottom row with keyboard shortcut and Clear button */}
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            pt: props.isMobile ? 0.5 : 1,
                                            pb: props.isMobile ? "env(safe-area-inset-bottom, 0)" : 0
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {!props.isMobile && (
                                                    <Typography variant={"caption"} sx={{ color: 'text.secondary' }}>
                                                        {operatingSystem === "Mac OS" ? "⌘ + Enter to send" : operatingSystem === "Windows" ? "Ctrl + Enter to send" : null}
                                                    </Typography>
                                                )}

                                               {process.env.REACT_APP_STAGE === 'dev' &&
                                                                                                <FormControl size="small" sx={{ minWidth: 'auto' }}>
                                                    <Select
                                                        value={selectedChannel}
                                                        onChange={(e) => setSelectedChannel(e.target.value)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            height: '24px',
                                                            minWidth: '110px',
                                                            '& .MuiSelect-select': {
                                                                padding: '2px 8px 2px 8px',
                                                                paddingRight: '24px !important',
                                                                fontSize: '0.7rem',
                                                                color: 'text.secondary'
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(0,0,0,0.1)'
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(0,0,0,0.2)'
                                                            },
                                                            '& .MuiSelect-icon': {
                                                                fontSize: '1rem',
                                                                color: 'text.secondary'
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="chat-stream" sx={{ fontSize: '0.75rem', minHeight: 'auto', py: 0.5 }}>
                                                            chat-stream
                                                        </MenuItem>
                                                        <MenuItem value="chat-stream-v2" sx={{ fontSize: '0.75rem', minHeight: 'auto', py: 0.5 }}>
                                                            chat-stream-v2
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                                }
                                            </Box>
                                            <Button
                                                onClick={handleClearConversation}
                                                disabled={messageHistory.length === 0}
                                                variant={"outlined"}
                                                size={props.isMobile ? "small" : "small"}
                                                sx={{ minWidth: 'auto' }}
                                            >
                                                Clear
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    }
                </Box>
            </Drawer>

            {props.showQPilotChat && (
                <>










                </>
            )}
            
            {/* Session Manager Drawer - Only visible in chat-stream-v2 mode */}
            {selectedChannel === "chat-stream-v2" && (
                <SwipeableDrawer
                    anchor={props.isMobile ? "bottom" : "left"}
                    open={showSessionManager}
                    onClose={() => setShowSessionManager(false)}
                    onOpen={() => setShowSessionManager(true)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: props.isMobile ? '100%' : 360,
                            height: props.isMobile ? '85vh' : '100%',
                            borderTopLeftRadius: props.isMobile ? 16 : 0,
                            borderTopRightRadius: props.isMobile ? 16 : 0,
                        }
                    }}
                >
                    <Box sx={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        height: "100%",
                        backgroundColor: "background.paper"
                    }}>
                        {/* Header */}
                        <Box sx={{
                            p: 2,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Chat History
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowSessionManager(false)}
                                sx={{ color: "text.secondary" }}
                            >
                                <AiOutlineCaretRight />
                            </IconButton>
                        </Box>

                        {/* Search and Filter */}
                        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                            <Paper
                                component="form"
                                sx={{ 
                                    p: '2px 4px', 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    backgroundColor: "action.hover",
                                    boxShadow: 0
                                }}
                            >
                                <IconButton sx={{ p: '10px' }} aria-label="search">
                                    <SearchIcon sx={{ color: "primary.main" }} />
                                </IconButton>
                                <InputBase
                                    sx={{ ml: 1, flex: 1 }}
                                    placeholder="Search chats..."
                                    value={sessionSearchQuery}
                                    onChange={(e) => setSessionSearchQuery(e.target.value)}
                                />
                            </Paper>
                            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                                <Chip
                                    label="All"
                                    size="small"
                                    onClick={() => setSessionFilter("all")}
                                    color={sessionFilter === "all" ? "primary" : "default"}
                                    variant={sessionFilter === "all" ? "filled" : "outlined"}
                                />
                                <Chip
                                    label="Active"
                                    size="small"
                                    onClick={() => setSessionFilter("active")}
                                    color={sessionFilter === "active" ? "primary" : "default"}
                                    variant={sessionFilter === "active" ? "filled" : "outlined"}
                                />
                                <Chip
                                    label="Archived"
                                    size="small"
                                    onClick={() => setSessionFilter("archived")}
                                    color={sessionFilter === "archived" ? "primary" : "default"}
                                    variant={sessionFilter === "archived" ? "filled" : "outlined"}
                                />
                            </Box>
                        </Box>

                        {/* Session List */}
                        <List sx={{ flex: 1, overflow: "auto" }}>
                            {filteredSessions.map((session) => {
                                const isActive = session.id === currentSessionId;
                                const isArchived = session.status === "archived";
                                
                                return (
                                    <ListItem
                                        key={session.id}
                                        disablePadding
                                        sx={{
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                            backgroundColor: isActive ? "action.selected" : "transparent",
                                            '&:hover': {
                                                backgroundColor: isActive ? "action.selected" : "action.hover"
                                            }
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => handleLoadSession(session.id)}
                                            sx={{ py: 2 }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isArchived ? "text.disabled" : "text.primary"
                                                        }}
                                                    >
                                                        {session.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: "block",
                                                                color: "text.secondary",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {session.preview}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: "block",
                                                                color: "text.disabled",
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            {new Date(session.lastModified).toLocaleDateString()} • {session.messageCount} messages
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <Tooltip title={isArchived ? "Unarchive" : "Archive"}>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleArchiveSession(session.id);
                                                        }}
                                                        sx={{ color: "text.secondary" }}
                                                    >
                                                        {isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* New Chat Button */}
                        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AddIcon sx={{ color: "inherit" }} />}
                                onClick={handleCreateNewSession}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 500
                                }}
                            >
                                New Chat
                            </Button>
                        </Box>
                    </Box>
                </SwipeableDrawer>
            )}
            
            {/* Drag and Drop Overlay */}
            <DragDropOverlay isDragActive={dragAndDrop.isDragActive} />
        </>
    )
}
